import { AxiosError, AxiosResponse } from 'axios';
import pathEq from 'ramda/es/pathEq';

import { KeyCloakError } from 'types/api';
import { isNetworkError } from 'utils/error';

import { AUTH_ERROR_DESCRIPTIONS, SESSION_EVENT_CHANNEL } from '../constants';
import { generateAccessToken, refreshAccessToken } from '../services';
import { AccessToken, AuthErrorCodes, LoginMutation } from '../types';
import { logout } from './logout';
import { saveLocalRefreshToken } from './refresh-token';
import { saveLocalAuthenticationToken } from './token';

const isEmailNotConfirmedError = pathEq(
  ['response', 'data', 'error_description'],
  AUTH_ERROR_DESCRIPTIONS.ACCOUNT_NOT_FULLY_SET_UP
);

const isInvalidCredentialsError = pathEq(['response', 'status'], 401);

const handleAuthError = (error: AxiosError<KeyCloakError>): AuthErrorCodes => {
  if (isEmailNotConfirmedError(error)) {
    return AuthErrorCodes.EMAIL_NOT_CONFIRMED;
  }

  if (isInvalidCredentialsError(error)) {
    return AuthErrorCodes.INVALID_CREDENTIALS;
  }

  if (isNetworkError(error)) {
    return AuthErrorCodes.NET_ERR;
  }

  return AuthErrorCodes.UNKNOWN;
};

export const replaceAccessTokens = (
  accessToken: string,
  expiresIn: number,
  refreshToken: string,
  refreshExpiresIn: number
): void => {
  saveLocalAuthenticationToken(accessToken, expiresIn);
  saveLocalRefreshToken(refreshToken, refreshExpiresIn);
};

export const login = (accessToken: string, expiresIn: number, refreshToken: string, refreshExpiresIn: number): void => {
  replaceAccessTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn);

  SESSION_EVENT_CHANNEL.emitTokenAdded();
};

export const tryLogin = ({ username, password }: LoginMutation, recaptchaToken: string): Promise<void> =>
  generateAccessToken(username, password, recaptchaToken)
    .then((response) => {
      const {
        access_token: accessToken,
        refresh_token: refreshToken,
        refresh_expires_in: refreshExpiresIn,
        expires_in: expiresIn,
      } = response.data;

      login(accessToken, expiresIn, refreshToken, refreshExpiresIn);
    })
    .catch((error) => {
      logout();

      return Promise.reject(handleAuthError(error));
    });

export const refreshSession = (): Promise<void> =>
  refreshAccessToken().then((response: AxiosResponse<AccessToken>) => {
    const {
      access_token: accessToken,
      refresh_token: refreshToken,
      refresh_expires_in: refreshExpiresIn,
      expires_in: expiresIn,
    } = response.data;

    replaceAccessTokens(accessToken, expiresIn, refreshToken, refreshExpiresIn);
    SESSION_EVENT_CHANNEL.emitTokenAdded();
  });
